import { useCallback, useEffect, useState } from 'react'

import { ProviderClient } from '@centivo/api-clients'

import { getProviderDomainUrlForContext } from 'src/context/functions/getAppConfig'
import { IPractitioner } from 'src/shared/types'

export type TPractitionerIdentifiers = {
  npi: string
  latitude: string
  longitude: string
  network: string
  state: string
  city: string
  zipCode: string
  address1: string
  address2: string | undefined
  practiceName: string
}

export const usePractitionerDetails = ({
  npi,
  key,
}: {
  npi: string
  key: string
}) => {
  const providerClient = new ProviderClient(getProviderDomainUrlForContext())

  const [practitionerDetails, setPractitionerDetails] = useState<
    IPractitioner | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const fetch = useCallback(() => {
    setIsLoading(true)
    setHasError(false)
    providerClient
      .getProvidersFetch({ npi, key })
      .then((result: { data: IPractitioner[] | null }) => {
        const practitioner = (result.data ?? [])[0]
        if (!practitioner) {
          setHasError(true)
          setIsLoading(false)
          return
        }

        setPractitionerDetails(practitioner)
        setIsLoading(false)
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any) => {
        setIsLoading(false)
        setHasError(true)
        console.error(error)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npi, key])

  useEffect(() => {
    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    practitionerDetails,
    isLoading,
    hasError,
    fetch,
  }
}

export const generatePractitionerDetailsKey = ({
  npi,
  network,
  state,
  city,
  zipCode,
  address1,
  address2,
  practiceName,
}: TPractitionerIdentifiers) => {
  const providerClient = new ProviderClient(getProviderDomainUrlForContext())

  const key = providerClient.generateDoctorKey(
    network,
    state,
    city,
    zipCode,
    address1,
    address2 === '' ? undefined : address2, // undefined defaults to null for address2 in generateDoctorKey(), which is how we store address2 in the key in the database if it's not specified
    practiceName
  )

  return {
    npi,
    key,
  }
}
